import { useQuery } from '@tanstack/react-query'
import { blockchainQueryKeys, useRankedTopStakingTowns } from 'use-towns-client'
import { Address, getAddress } from 'viem'

interface UseReputationDataParams {
    spaceAddress?: Address
    enabled?: boolean
}

export const useReputationData = ({ spaceAddress, enabled = true }: UseReputationDataParams) => {
    const {
        data: topStakingTownsData,
        isLoading: isLoadingTopStakingTowns,
        error: errorTopStakingTowns,
    } = useRankedTopStakingTowns({
        enabled,
    })

    const { error, data } = useQuery({
        queryKey: blockchainQueryKeys.stakingReputationByTown(spaceAddress),
        queryFn: () => {
            if (!topStakingTownsData || !spaceAddress) {
                return undefined
            }
            const townData = topStakingTownsData[spaceAddress.toLowerCase()]
            if (!townData) {
                return undefined
            }

            return {
                totalStaked: BigInt(townData.totalAmountStaked),
                rank: townData.rank,
                total: Object.keys(topStakingTownsData).length,
            }
        },
        enabled: !!topStakingTownsData && !!spaceAddress,
    })

    return {
        data,
        isLoading: isLoadingTopStakingTowns,
        error: errorTopStakingTowns || error,
        hasData: !!data,
    }
}

interface UseTopTownsParams {
    enabled?: boolean
}

export const useTopStakingTowns = ({ enabled = true }: UseTopTownsParams = {}) => {
    const {
        data: topStakingTownsData,
        isLoading: isLoadingTopStakingTowns,
        error: errorTopStakingTowns,
    } = useRankedTopStakingTowns({
        enabled,
    })

    const { error, data: sortedSpaces } = useQuery({
        queryKey: blockchainQueryKeys.stakingTopTowns(),
        queryFn: () => {
            if (!topStakingTownsData) {
                return null
            }

            const stakingEntries = Object.entries(topStakingTownsData)
            return stakingEntries.map(([id, townData]) => ({
                address: getAddress(id),
                totalStaked: BigInt(townData.totalAmountStaked),
                rank: townData.rank,
                total: stakingEntries.length,
            }))
        },
        enabled: !!topStakingTownsData && enabled,
    })

    return {
        data: sortedSpaces,
        isLoading: isLoadingTopStakingTowns,
        error: errorTopStakingTowns || error,
        hasData: !!sortedSpaces && sortedSpaces.length > 0,
    }
}
